import { useState, useEffect } from 'react'
import { Prescription } from '../types/prescription'
import { Patient } from '../types/patient'
import { User } from '../types/user'
import { PracticeLocation } from '../types/practiceLocation'
import { getAgeDisplay, isUnder12 } from '../utils/ageUtils'
import { formatDate } from '../utils/locale'
import { userService } from '../services/userService'
import './PrescriptionPrintView.css'

interface PrescriptionPrintViewProps {
  prescription: Prescription
  patient: Patient
  prescriber: User
  practiceLocation?: PracticeLocation
}

export function PrescriptionPrintView({
  prescription,
  patient,
  prescriber,
  practiceLocation,
}: PrescriptionPrintViewProps) {
  // Get signature URL from prescriber object if available, otherwise fetch it
  const [signatureUrl, setSignatureUrl] = useState<string | null>(
    (prescriber as any).signatureUrl || null
  )
  
  // Load signature from S3 if signatureS3Key exists and URL not already provided
  useEffect(() => {
    // If signatureUrl was already provided (from print window), use it
    if ((prescriber as any).signatureUrl) {
      setSignatureUrl((prescriber as any).signatureUrl)
      return
    }
    
    // Otherwise, fetch it if signatureS3Key exists
    if (prescriber.signatureS3Key && prescriber.id) {
      userService.getSignatureUrl(prescriber.id)
        .then(url => setSignatureUrl(url))
        .catch(err => {
          console.warn('Failed to load signature:', err)
          setSignatureUrl(null)
        })
    } else {
      setSignatureUrl(null)
    }
  }, [prescriber.signatureS3Key, prescriber.id, (prescriber as any).signatureUrl])
  
  const patientAge = getAgeDisplay(patient.dateOfBirth)
  const showAgeInMonths = isUnder12(patient.dateOfBirth)
  const prescriptionDate = formatDate(prescription.prescriptionDate)
  const address = patient.address
    ? `${patient.address.street || ''}, ${patient.address.city || ''}, ${patient.address.postalCode || ''}`.trim()
    : ''

  // Get primary practice address
  const primaryAddress = practiceLocation?.addresses?.find((addr) => addr.type === 'primary' && addr.isActive)
  const practiceAddress = primaryAddress
    ? [
        primaryAddress.addressLine1,
        primaryAddress.addressLine2,
        primaryAddress.city,
        primaryAddress.county,
        primaryAddress.postcode,
      ]
        .filter(Boolean)
        .join(', ')
    : ''

  return (
    <div className="prescription-print-view">
      {/* Practice Header */}
      {practiceLocation && (
        <div className="practice-header">
          <div className="practice-name">{practiceLocation.practiceName}</div>
          {practiceAddress && <div className="practice-address">{practiceAddress}</div>}
        </div>
      )}

      <div className="prescription-header">
        <div className="prescription-header-left">
          <h1 className="prescription-title">
            {prescription.prescriptionType.toUpperCase()} PRESCRIPTION
            {prescription.scriptType === 'repeat' && ' - REPEAT'}
          </h1>
        </div>
        <div className="prescription-header-right">
          <div className="prescription-date">Date: {prescriptionDate}</div>
        </div>
      </div>

      <div className="prescription-body">
        {/* Patient Information */}
        <div className="prescription-section patient-section">
          <h2 className="section-title">Patient Information</h2>
          <div className="patient-info-grid">
            <div className="info-item">
              <span className="info-label">Name:</span>
              <span className="info-value">
                {patient.firstName} {patient.lastName}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Date of Birth:</span>
              <span className="info-value">
                {formatDate(patient.dateOfBirth)}
              </span>
            </div>
            {showAgeInMonths && (
              <div className="info-item">
                <span className="info-label">Age:</span>
                <span className="info-value">{patientAge}</span>
              </div>
            )}
            {patient.gmsNumber && (
              <div className="info-item">
                <span className="info-label">GMS Number:</span>
                <span className="info-value">{patient.gmsNumber}</span>
              </div>
            )}
            {patient.ppsNumber && (
              <div className="info-item">
                <span className="info-label">PPS Number:</span>
                <span className="info-value">{patient.ppsNumber}</span>
              </div>
            )}
            {address && (
              <div className="info-item full-width">
                <span className="info-label">Address:</span>
                <span className="info-value">{address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Medications */}
        <div className="prescription-section medications-section">
          <h2 className="section-title">Medications</h2>
          <table className="medications-table">
            <thead>
              <tr>
                <th className="med-number">#</th>
                <th className="med-name">Medication</th>
                <th className="med-dose">Dose</th>
                <th className="med-route">Route</th>
                <th className="med-frequency">Frequency</th>
                <th className="med-quantity">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {prescription.medications.map((med, idx) => (
                <tr key={med.medicationId}>
                  <td className="med-number">{idx + 1}</td>
                  <td className="med-name">
                    <div className="med-name-primary">{med.name}</div>
                    {med.gmsCode && (
                      <div className="med-gms-code">GMS: {med.gmsCode}</div>
                    )}
                    {med.instructions && (
                      <div className="med-instructions">{med.instructions}</div>
                    )}
                    {med.indication && (
                      <div className="med-indication">For: {med.indication}</div>
                    )}
                  </td>
                  <td className="med-dose">{med.dose || '-'}</td>
                  <td className="med-route">{med.route || '-'}</td>
                  <td className="med-frequency">{med.frequency || '-'}</td>
                  <td className="med-quantity">
                    {med.quantity.value} {med.quantity.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* General Instructions */}
        {prescription.instructions && (
          <div className="prescription-section instructions-section">
            <h2 className="section-title">Instructions</h2>
            <div className="instructions-content">{prescription.instructions}</div>
          </div>
        )}

        {/* Repeat Prescription Information */}
        {prescription.scriptType === 'repeat' && (
          <div className="prescription-section repeat-section">
            <h2 className="section-title">Repeat Prescription Details</h2>
            <div className="repeat-info-grid">
              {prescription.maxRepeats !== undefined && (
                <div className="info-item">
                  <span className="info-label">Maximum Repeats:</span>
                  <span className="info-value">{prescription.maxRepeats}</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Current Repeat:</span>
                <span className="info-value">
                  {prescription.currentRepeatCount}
                  {prescription.maxRepeats !== undefined &&
                    ` of ${prescription.maxRepeats}`}
                </span>
              </div>
              {prescription.reviewDate && (
                <div className="info-item">
                  <span className="info-label">Review Date:</span>
                  <span className="info-value">
                    {formatDate(prescription.reviewDate)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Handwritten Notes */}
        {prescription.isHandwritten && prescription.handwrittenNotes && (
          <div className="prescription-section handwritten-section">
            <h2 className="section-title">Handwritten Notes</h2>
            <div className="handwritten-content">{prescription.handwrittenNotes}</div>
          </div>
        )}

        {/* Prescriber Information and Signature */}
        <div className="prescription-section prescriber-section">
          <div className="prescriber-info">
            <div className="prescriber-details">
              <div className="prescriber-name">
                <strong>{prescriber.firstName} {prescriber.lastName}</strong>
              </div>
              {prescriber.professionalBodyRegistrationNumber && (
                <div className="prescriber-registration">
                  Medical Council Registration: {prescriber.professionalBodyRegistrationNumber}
                </div>
              )}
              {prescriber.gmsNumber && (
                <div className="prescriber-gms">
                  GMS Number: {prescriber.gmsNumber}
                </div>
              )}
            </div>
            <div className="prescriber-signature">
              {signatureUrl ? (
                <div className="signature-image-container">
                  <img 
                    src={signatureUrl} 
                    alt="Prescriber signature" 
                    className="signature-image-display"
                    onError={(e) => {
                      console.error('Failed to load signature image from S3')
                      e.currentTarget.style.display = 'none'
                      setSignatureUrl(null)
                    }}
                  />
                  <div className="signature-label">Prescriber Signature</div>
                </div>
              ) : (
                <>
                  <div className="signature-line"></div>
                  <div className="signature-label">Prescriber Signature</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="prescription-footer">
        <div className="footer-note">
          This prescription was generated electronically on {formatDate(new Date().toISOString())}
        </div>
        {prescription.printedAt && (
          <div className="footer-note">
            Printed: {formatDate(prescription.printedAt)}
          </div>
        )}
      </div>
    </div>
  )
}

